import React, { createContext, useContext, useState, useEffect } from 'react'
import { apiService } from '../services/apiService'

const ApiContext = createContext()

export const useApi = () => {
  const context = useContext(ApiContext)
  if (!context) {
    throw new Error('useApi deve ser usado dentro de ApiProvider')
  }
  return context
}

export const ApiProvider = ({ children }) => {
  const [loading, setLoading] = useState(false)
  const [whatsappStatus, setWhatsappStatus] = useState(null)
  const [systemHealth, setSystemHealth] = useState(null)

  // ==========================================
  // FUNÇÕES DE VERIFICAÇÃO
  // ==========================================

  /**
   * Verificar saúde do sistema
   */
  const checkHealth = async () => {
    try {
      const response = await apiService.system.getHealth()
      const health = response.data || response
      setSystemHealth(health)
      return health
    } catch (error) {
      console.error('❌ Erro ao verificar saúde do sistema:', error)
      setSystemHealth({ 
        success: false,
        status: 'error', 
        message: error.message,
        database: { conectado: false }
      })
      return { success: false, error: error.message }
    }
  }

  /**
   * ✅ CORRIGIDO: Verificar status do WhatsApp
   * Agora lê corretamente o campo 'connected' do whatsapp-web.js (igual à página Configurações)
   */
  const getStatusWhatsApp = async () => {
    try {
      const response = await apiService.whatsapp.getStatus()
      
      console.log('🔍 Status WhatsApp RAW (ApiContext):', response.data)
      
      // ✅ LÓGICA IGUAL À PÁGINA CONFIGURAÇÕES
      // O backend retorna: { success: true, data: { connected: true/false, state: "CONNECTED", ... } }
      const estaConectado = response.data?.success && response.data?.data?.connected === true
      
      const data = response.data?.data || {}
      
      console.log('📱 Status WhatsApp processado (ApiContext):', { 
        estaConectado, 
        success: response.data?.success,
        connected: data?.connected,
        state: data?.state 
      })
      
      const novoStatus = {
        conectado: estaConectado,
        state: data?.state || 'DISCONNECTED',
        initializing: data?.initializing || false,
        hasQR: data?.hasQR || false,
        info: data?.data?.me ? {
          number: data.data.me.user,
          name: data.data.me.pushname || 'WhatsApp'
        } : null,
        timestamp: new Date().toISOString()
      }
      
      console.log('✅ Status final (ApiContext):', novoStatus)
      
      setWhatsappStatus(novoStatus)
      
      return novoStatus
    } catch (error) {
      console.error('❌ Erro ao verificar status WhatsApp (ApiContext):', error)
      const errorStatus = {
        conectado: false,
        state: 'ERROR',
        error: error.message
      }
      setWhatsappStatus(errorStatus)
      
      return errorStatus
    }
  }

  /**
   * Iniciar agendamentos do sistema
   */
  const iniciarAgendamentos = async () => {
    try {
      const response = await apiService.system.startScheduler()
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao iniciar agendamentos:', error)
      throw error
    }
  }

  // ==========================================
  // FUNÇÕES DO DASHBOARD
  // ==========================================

  /**
   * Obter estatísticas do dashboard
   */
  const getDashboardStats = async () => {
    try {
      setLoading(true)
      const response = await apiService.dashboard.getStats()
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao buscar estatísticas:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Obter vendas do mês por vendedor, comparadas ao mesmo período do ano anterior
   */
  const getVendasMesVendedor = async (filial = 1) => {
    try {
      setLoading(true)
      const response = await apiService.dashboard.getVendasMesVendedor(filial)
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao buscar vendas do mês por vendedor:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Obter índice de inadimplência geral e por vendedor (janelas de 1/2/3 anos)
   */
  const getIndiceInadimplencia = async (filial = 1) => {
    try {
      setLoading(true)
      const response = await apiService.inadimplencia.getIndice(filial)
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao buscar índice de inadimplência:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // ==========================================
  // FUNÇÕES DE COBRANÇA
  // ==========================================

  /**
   * ✅ CORRIGIDO: Buscar clientes em atraso
   * @param {number} diasMin - Dias mínimos de atraso (padrão: 30)
   * @param {number} diasMax - Dias máximos de atraso (padrão: null)
   * @param {number} filial - Filial (padrão: 1)
   */
  const getClientesEmAtraso = async (diasMin = 30, diasMax = null, filial = 1) => {
    try {
      setLoading(true)
      const response = await apiService.cobranca.getClientesEmAtraso(diasMin, diasMax, filial)
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao buscar clientes em atraso:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * ✅ NOVO: Salvar dados de cobrança
   */
  const salvarDadosCobranca = async (dados) => {
    try {
      setLoading(true)
      const response = await apiService.cobranca.salvarDadosCobranca(dados)
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao salvar dados de cobrança:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * ✅ CORRIGIDO: Enviar cobrança via WhatsApp
   * Agora aceita mensagem customizável
   */
  const enviarCobrancaWhatsApp = async (dados) => {
    try {
      setLoading(true)
      
      // dados = { ordem_cliente, telefone, mensagem }
      const mensagem = dados.mensagem || `Olá! Identificamos que você possui débitos em aberto. Por favor, regularize sua situação o quanto antes. Em caso de dúvidas, entre em contato conosco.`
      
      const response = await apiService.whatsapp.enviarMensagem(dados.telefone, mensagem)
      
      // Registrar envio no histórico
      if (response.data?.success || response.success) {
        await apiService.cobranca.registrarEnvioWhatsApp({
          ordem_cliente: dados.ordem_cliente,
          telefone: dados.telefone,
          mensagem: mensagem,
          sucesso: true
        })
      }
      
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao enviar cobrança WhatsApp:', error)
      
      // Registrar falha no histórico
      try {
        await apiService.cobranca.registrarEnvioWhatsApp({
          ordem_cliente: dados.ordem_cliente,
          telefone: dados.telefone,
          mensagem: dados.mensagem || '',
          sucesso: false,
          erro: error.message
        })
      } catch (logError) {
        console.error('Erro ao registrar falha:', logError)
      }
      
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * ✅ NOVO: Buscar agendamentos do dia
   */
  const getAgendamentosDia = async (data = null) => {
    try {
      setLoading(true)
      const response = await apiService.cobranca.getAgendamentos(data)
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao buscar agendamentos:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * 🆕 NOVO: Buscar agenda de cobranças (período da semana)
   * @param {string} dataInicial - Data inicial no formato YYYY-MM-DD
   * @param {string} dataFinal - Data final no formato YYYY-MM-DD
   */
  const getAgendaCobrancas = async (dataInicial, dataFinal) => {
    try {
      setLoading(true)
      console.log('📅 Buscando agenda de cobranças:', { dataInicial, dataFinal })
      
      const response = await apiService.cobranca.getAgendaCobrancas(dataInicial, dataFinal)
      
      console.log('✅ Agenda de cobranças recebida:', response.data || response)
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao buscar agenda de cobranças:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * ✅ NOVO: Buscar estatísticas de cobrança
   */
  const getEstatisticasCobranca = async (filial = 1) => {
    try {
      setLoading(true)
      const response = await apiService.cobranca.getEstatisticas(filial)
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao buscar estatísticas de cobrança:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Enviar cobrança automática (legado - manter compatibilidade)
   */
  const enviarCobrancaAutomatica = async () => {
    try {
      setLoading(true)
      const response = await apiService.cobranca.executarAutomatica()
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao enviar cobrança automática:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Enviar cobrança individual (legado - manter compatibilidade)
   */
  const enviarCobrancaIndividual = async (clienteId, mensagem) => {
    try {
      setLoading(true)
      const response = await apiService.cobranca.enviarManual(clienteId, mensagem)
      return response
    } catch (error) {
      console.error('❌ Erro ao enviar cobrança individual:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Buscar relatório presencial
   */
  const getRelatorioPresencial = async (diasAtraso = 90, filial = 1) => {
    try {
      setLoading(true)
      const response = await apiService.cobranca.gerarListaImpressao(diasAtraso, filial)
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao buscar relatório presencial:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Testar sistema
   */
  const testarSistema = async () => {
    try {
      setLoading(true)
      const response = await apiService.cobranca.testar()
      return response.data || response
    } catch (error) {
      console.error('❌ Erro ao testar sistema:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // ==========================================
  // VERIFICAÇÕES INICIAIS
  // ==========================================

  useEffect(() => {
    const initChecks = async () => {
      console.log('🚀 ApiContext: Inicializando verificações...')
      
      await Promise.all([
        getStatusWhatsApp(),
        checkHealth()
      ])
      
      console.log('✅ ApiContext: Verificações iniciais concluídas')
      console.log('📱 Status WhatsApp após init:', whatsappStatus)
    }
    
    initChecks()
    
    // ✅ Verificar status a cada 30 segundos (mais frequente)
    const interval = setInterval(() => {
      console.log('🔄 ApiContext: Verificação periódica (30s)...')
      getStatusWhatsApp()
      checkHealth()
    }, 30 * 1000) // 30 segundos

    return () => clearInterval(interval)
  }, [])

  // ==========================================
  // CONTEXT VALUE
  // ==========================================

  const value = {
    loading,
    setLoading,
    whatsappStatus,
    systemHealth,
    
    // Funções de verificação
    checkHealth,
    getStatusWhatsApp,
    iniciarAgendamentos,
    
    // Funções do dashboard
    getDashboardStats,
    getVendasMesVendedor,
    getIndiceInadimplencia,

    // Funções de cobrança
    getClientesEmAtraso,
    salvarDadosCobranca,
    enviarCobrancaWhatsApp,
    getAgendamentosDia,
    getAgendaCobrancas,
    getEstatisticasCobranca,
    testarSistema,
    enviarCobrancaAutomatica,
    enviarCobrancaIndividual,
    getRelatorioPresencial,
    
    // API service direto (para casos específicos)
    apiService
  }

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  )
}